import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem('authToken');
      setIsAuthenticated(!!token);
    };
    
    checkAuthStatus();
    window.addEventListener('storage', checkAuthStatus);
    
    return () => window.removeEventListener('storage', checkAuthStatus);
  }, []);

  const navigationItems = [
    { label: 'Dashboard', path: '/user-dashboard', icon: 'LayoutDashboard' },
    { label: 'Upload', path: '/photo-upload', icon: 'Upload' },
    { label: 'Recommendations', path: '/upgrade-recommendations', icon: 'Zap' },
    { label: 'Find Shops', path: '/local-shop-finder', icon: 'MapPin' },
    { label: 'Wishlist', path: '/wishlist', icon: 'Heart' },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    setIsAuthenticated(false);
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    if (path === '/upgrade-recommendations') {
      return location?.pathname === '/upgrade-recommendations' || location?.pathname === '/bike-analysis-results';
    }
    return location?.pathname === path;
  };

  const Logo = () => (
    <div className="flex items-center space-x-3">
      <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center shadow-md">
        <div className="text-white font-bold text-lg">T</div>
      </div>
      <span className="text-xl font-bold text-primary">trick.bike</span>
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div 
            className="cursor-pointer interactive-scale"
            onClick={() => handleNavigation(isAuthenticated ? '/user-dashboard' : '/')}
          >
            <Logo />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {isAuthenticated && navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`nav-item interactive-scale ${
                  isActivePath(item?.path) ? 'active' : ''
                }`}
              >
                <Icon name={item?.icon} size={18} className="mr-2" />
                {item?.label}
              </button>
            ))}
          </nav>

          {/* Desktop Auth Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Settings"
                  iconPosition="left"
                  onClick={() => navigate('/settings')}
                >
                  Settings
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="LogOut"
                  iconPosition="left"
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleNavigation('/login')}
                >
                  Login
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleNavigation('/register')}
                >
                  Sign Up
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 interactive-scale"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <Icon 
              name={isMobileMenuOpen ? "X" : "Menu"} 
              size={24} 
              color="var(--color-primary)" 
            />
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-border animate-fade-in">
            <nav className="flex flex-col space-y-2 mt-4">
              {isAuthenticated && navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`flex items-center px-3 py-3 text-sm font-medium rounded-md transition-colors interactive-scale ${
                    isActivePath(item?.path)
                      ? 'bg-accent text-accent-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={18} className="mr-3" />
                  {item?.label}
                </button>
              ))}
              
              {/* Mobile Auth Actions */}
              <div className="pt-4 border-t border-border mt-4">
                {isAuthenticated ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        navigate('/settings');
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center w-full px-3 py-3 text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors interactive-scale"
                    >
                      <Icon name="Settings" size={18} className="mr-3" />
                      Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-3 py-3 text-sm font-medium text-error hover:bg-error/10 rounded-md transition-colors interactive-scale"
                    >
                      <Icon name="LogOut" size={18} className="mr-3" />
                      Logout
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={() => handleNavigation('/login')}
                      className="flex items-center w-full px-3 py-3 text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors interactive-scale"
                    >
                      <Icon name="LogIn" size={18} className="mr-3" />
                      Login
                    </button>
                    <button
                      onClick={() => handleNavigation('/register')}
                      className="flex items-center w-full px-3 py-3 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors interactive-scale"
                    >
                      <Icon name="UserPlus" size={18} className="mr-3" />
                      Sign Up
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
      {/* Mobile Upload FAB - Only show on mobile when authenticated and not on upload page */}
      {isAuthenticated && location?.pathname !== '/photo-upload' && (
        <button
          onClick={() => handleNavigation('/photo-upload')}
          className="lg:hidden fixed bottom-6 right-6 w-14 h-14 bg-accent text-accent-foreground rounded-full shadow-lg flex items-center justify-center z-40 interactive-scale elevation-2"
          aria-label="Upload photo"
        >
          <Icon name="Camera" size={24} />
        </button>
      )}
    </header>
  );
};

export default Header;